import posthog from "posthog-js";
import { isPostHogEnabled } from "@/config/posthog";
import { PostHogEvents } from "./events";
import type { PostHogEventName } from "./events";
import type {
  ButtonClickedProperties,
  PageDroppedProperties,
  VerifyEmailModalProperties,
  OtpVerificationProperties,
  LoginSucceededProperties,
  LoginFailedProperties,
  MyAccountPageViewedProperties,
  BookAppointmentPageViewedProperties,
  BookNewAppointmentClickedProperties,
  ApplicationFormStartedProperties,
  ApplicationFormStepCompletedProperties,
  ApplicationFormSubmittedProperties,
  SlotSelectionPageViewedProperties,
  SlotSelectedProperties,
  SlotSelectionConfirmedProperties,
  ImageUploadedProperties,
  LivenessPageViewedProperties,
  LivenessCheckSucceededProperties,
  LivenessCheckFailedProperties,
  LivenessCheckDroppedProperties,
  BookingCompletedProperties,
  AppointmentCancelledProperties,
  AppointmentLetterDownloadedProperties,
} from "./properties";

/**
 * Typed payloads for events that carry structured properties.
 * Events omitted here accept optional arbitrary metadata via the fallback overload.
 */
export interface PostHogEventPropertyMap {
  [PostHogEvents.SIGNUP_CTA_CLICKED]: ButtonClickedProperties;
  [PostHogEvents.VERIFY_EMAIL_MODAL_SHOWN]: VerifyEmailModalProperties;
  [PostHogEvents.OTP_VERIFICATION_SUCCEEDED]: OtpVerificationProperties;
  [PostHogEvents.LOGIN_SUCCEEDED]: LoginSucceededProperties;
  [PostHogEvents.FIRST_SIGNIN_SUCCEEDED]: LoginSucceededProperties;
  [PostHogEvents.LOGIN_FAILED]: LoginFailedProperties;
  [PostHogEvents.MY_ACCOUNT_PAGE_VIEWED]: MyAccountPageViewedProperties;
  [PostHogEvents.MY_ACCOUNT_DROPPED]: PageDroppedProperties;
  [PostHogEvents.SIGNUP_PAGE_DROPPED]: PageDroppedProperties;
  [PostHogEvents.BOOK_APPOINTMENT_PAGE_VIEWED]: BookAppointmentPageViewedProperties;
  [PostHogEvents.BOOK_NEW_APPOINTMENT_CLICKED]: BookNewAppointmentClickedProperties;
  [PostHogEvents.APPLICATION_FORM_STARTED]: ApplicationFormStartedProperties;
  [PostHogEvents.APPLICATION_FORM_STEP_COMPLETED]: ApplicationFormStepCompletedProperties;
  [PostHogEvents.APPLICATION_FORM_SUBMITTED]: ApplicationFormSubmittedProperties;
  [PostHogEvents.SLOT_SELECTION_PAGE_VIEWED]: SlotSelectionPageViewedProperties;
  [PostHogEvents.SLOT_SELECTED]: SlotSelectedProperties;
  [PostHogEvents.SLOT_SELECTION_CONFIRMED]: SlotSelectionConfirmedProperties;
  [PostHogEvents.IMAGE_UPLOADED]: ImageUploadedProperties;
  [PostHogEvents.LIVENESS_PAGE_VIEWED]: LivenessPageViewedProperties;
  [PostHogEvents.LIVENESS_CHECK_SUCCEEDED]: LivenessCheckSucceededProperties;
  [PostHogEvents.LIVENESS_CHECK_FAILED]: LivenessCheckFailedProperties;
  [PostHogEvents.LIVENESS_CHECK_DROPPED]: LivenessCheckDroppedProperties;
  [PostHogEvents.BOOKING_COMPLETED]: BookingCompletedProperties;
  [PostHogEvents.BOOKING_SUMMARY_DROPPED]: PageDroppedProperties;
  [PostHogEvents.APPOINTMENT_CANCELLED]: AppointmentCancelledProperties;
  [PostHogEvents.APPOINTMENT_LETTER_DOWNLOADED]: AppointmentLetterDownloadedProperties;
}

export type PostHogTypedEventName = keyof PostHogEventPropertyMap;

export function captureEvent<K extends PostHogTypedEventName>(
  event: K,
  properties: PostHogEventPropertyMap[K],
): void;
export function captureEvent(
  event: Exclude<PostHogEventName, PostHogTypedEventName> | (string & {}),
  properties?: Record<string, unknown>,
): void;
export function captureEvent(
  event: PostHogEventName | (string & {}),
  properties?: Record<string, unknown>,
): void {
  if (!isPostHogEnabled()) return;
  posthog.capture(event, properties);
}
